import { Body2 } from '@components/typography/Body2.tsx';
import { Body4 } from '@components/typography/Body4.tsx';
import { Button } from '@components/Button.tsx';
import { ButtonType } from '@common/types/button';
import {
  type ComponentType,
  type PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import { ValidationResult } from '@funnellab/shared/src/validation.ts';
import { classNames } from '@utils/classNames.ts';
import { useTranslation } from 'react-i18next';
import Combobox from '@components/input/Combobox.tsx';

export function InlineComboboxEdit<Option>({
  value,
  onSubmit,
  options,
  displayValue,
  placeholder,
  className,
  ValueComponent = Body2,
  testId,
  emptyDisplayValue,
  showButtons = true,
}: {
  value: Option;
  onSubmit: (value: Option) => Promise<void>;
  options: Option[];
  displayValue: (value: Option) => string;
  placeholder?: string;
  className?: string;
  ValueComponent?: ComponentType<PropsWithChildren & { className?: string }>;
  testId?: string;
  emptyDisplayValue?: string;
  showButtons?: boolean;
  classNameInput?: string;
}) {
  const [editedValue, setEditedValue] = useState<Option | null>(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('common');

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditedValue(value);
    setIsEditing(false);
  }, [value]);

  const submitEditedValue = useCallback(async () => {
    setError(null);
    if (editedValue === value) {
      setIsEditing(false);
      return;
    }

    if (!editedValue) {
      cancelEditing();
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(editedValue);
      setIsEditing(false);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : t('errors.unknown');
      setError(t('errors.errorWhileSaving', { errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  }, [cancelEditing, editedValue, onSubmit, t, value]);

  return isEditing ? (
    <div
      className={classNames(
        'group hover:bg-gray-100 p-1 rounded flex gap-2.5',
        showButtons
          ? 'flex-col justify-end w-full'
          : 'items-center justify-stretch',
        className,
      )}
    >
      <Combobox<Option>
        title={''}
        TitleComponent={Body4}
        placeholder={placeholder}
        options={options}
        displayValue={displayValue}
        value={editedValue}
        onChange={setEditedValue}
        required
        showDropdownButton
        leadingIcon={null}
        classNameContainer="flex-1 min-w-40"
        validate={(country) =>
          country ? ValidationResult.VALID : ValidationResult.EMPTY
        }
        showError={!!error}
        disabled={isSubmitting}
        classNameLabel="!mb-1"
        onBlur={cancelEditing}
      />
      {showButtons ? (
        <div className="flex gap-4 self-end max-w-full">
          <Button
            label={t('button.cancel')}
            buttonType={ButtonType.SECONDARY}
            // /**
            //  * We need to prevent the onBlur event when clicking this button, otherwise we save the value even when pressing the cancel button
            //  * https://stackoverflow.com/a/57630197
            //  * That's why we prevent onMouseDown that the onBlur event is triggered and the onClick event can be called
            //  */
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancelEditing}
            className="!px-4 !py-1.5 flex-shrink min-w-0"
            labelClassName="overflow-ellipsis block whitespace-nowrap overflow-hidden"
          />
          <Button
            label={t('button.save')}
            buttonType={ButtonType.PRIMARY}
            className="!px-4 !py-1.5 flex-shrink min-w-0"
            labelClassName="overflow-ellipsis block whitespace-nowrap overflow-hidden"
            // Prevents onBlur from being triggered if we want to press the save button
            onMouseDown={(e) => e.preventDefault()}
            onClick={submitEditedValue}
            data-testid="save-button"
          />
        </div>
      ) : (
        <div className="hidden group-hover:block">
          <Icon
            type={IconType.Pencil}
            className="w-6 h-6 text-slate-900 shrink-0"
          />
        </div>
      )}
    </div>
  ) : (
    <button
      className={classNames(
        'group hover:bg-gray-100 cursor-pointer flex justify-end md:justify-between items-center gap-2.5 text-left -ml-1 p-1 rounded',
        className,
      )}
      onClick={startEditing}
      data-testid={testId}
    >
      <ValueComponent className="break-words line-clamp-2">
        {displayValue(value) || emptyDisplayValue}
      </ValueComponent>
      <div
        className={classNames(
          'block md:hidden group-hover:block',
          showButtons &&
            'bg-white group-hover:border-blue-50 border-transparent border rounded -my-0.5',
        )}
      >
        <Icon
          type={IconType.Pencil}
          className="w-6 h-6 text-slate-900 shrink-0"
        />
      </div>
    </button>
  );
}
